// <copyright file="auth-interceptor.ts" company="BD">
// Copyright (c) BD. All rights reserved.
// Created: 12/29/2025 by Sumanth Satheesh
// </copyright>

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from "@angular/common/http";

import { inject, Injectable } from "@angular/core";
import { AuthTokenStore } from "../../shared/stores/auth-token-store";
import { catchError, Observable, throwError } from "rxjs";
import { AuthService } from "./auth-service";
import { CsrfTokenStoreService } from "../../shared/stores/csrf-token-store-service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private static readonly csrfExcludedUrlParts: readonly string[] = [
    "auth/login",
    "auth/logout",
    "auth/web-login?clientType=web-app"
  ];
  private authTokenStore = inject(AuthTokenStore);
  private authService = inject(AuthService);
  private csrfTokenStore = inject(CsrfTokenStoreService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.authTokenStore.getAccessToken();
    const csrf = this.csrfTokenStore.getToken();
    let headers = req.headers;
    headers = headers.set("Accept", "*/*");
    const isTokenReq = req.url.includes("csrftoken");
    const isCSRFExcluded = isTokenReq || this.isCsrfExcludedRequest(req.url);
    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }
    if (!isCSRFExcluded && csrf) {
      headers = headers.set("X-XSRF-TOKEN", csrf);
    }
    const authReq = req.clone({
      headers,
      withCredentials: !isCSRFExcluded
    });
    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.authService.logout();
        }
        return throwError(() => error);
      })
    );
  }

  private isCsrfExcludedRequest(url: string): boolean {
    const normalizedUrl = url.toLowerCase();
    return AuthInterceptor.csrfExcludedUrlParts.some((api) => normalizedUrl.includes(api));
  }
}
